import React, { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import UserCard from '../UserCard';
import { useChangeHiringBoardStatusMutation, useGetHiringBoardListQuery } from 'services';
import { useQueryClient } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import { HiringBoardInterface } from 'constants/interfaces';
import { SingleValue } from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { isObjectEmpty } from 'utils';
import useCommonStore, { setModalConfig } from 'store/common';
import toast from 'react-hot-toast';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

interface MyComponentProps {
	title: string;
	jobStatus: string;
	titleColor: string;
}

const HiringBoardCard: React.FC<MyComponentProps> = ({ title, titleColor, jobStatus }) => {
	const { id: jobID } = useParams<any>();
	const queryClient = useQueryClient();
	const { translate } = useTranslate();
	const [currentPage, setCurrentPage] = useState(1);
	const order = 'DESC';
	const { data: { docs = [], meta = [] } = {}, isFetched } = useGetHiringBoardListQuery({
		currentPage,
		order,
		jobStatus,
		jobID,
	});
	const { visible } = useCommonStore((state) => state?.modalConfig);
	const [jobStatusVal, setJobStatusVal] = useState('');

	const navigate = useNavigate();

	const { mutateAsync: changeStatusMutation } = useChangeHiringBoardStatusMutation();

	const loadMoreFunc = () => {
		setCurrentPage((prev) => prev + 1);
	};

	useEffect(() => {
		if (!visible) {
			queryClient
				.invalidateQueries({ queryKey: [jobStatusVal] })
				.then(() => {})
				.catch(() => {});
		}
	}, [visible]);

	useEffect(() => {
		if (isFetched && isObjectEmpty(meta)) {
			navigate(-1);
		}
	}, [isFetched]);

	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: [jobStatus] });
	}, [currentPage]);

	const changeStatus = (data: SingleValue<HiringBoardInterface>, id: string) => {
		changeStatusMutation({ data: { status: data?.value }, id })
			.then((_res: any) => {
				queryClient.invalidateQueries({ queryKey: [jobStatus] });
				queryClient.invalidateQueries({ queryKey: [data?.value] });
			})
			.catch((error) => {
				console.error('Failed to change status:', error);
				toast.error(translate('ToastMessage.failedToChangeStatus')); // Notify the user of the failure
			});
	};

	const rejectCandidateFunction = (id: string, currentJobStatus: string) => {
		setJobStatusVal(currentJobStatus);
		setModalConfig({
			visible: true,
			id: id,
			type: 'rejectModal',
		});
	};

	return (
		<div className="applicants-list flex-1">
			<div className="applicants-list-header d-flex align-items-center justify-content-between gap-2 mb-4">
				<div className={`applicants-list-title ${titleColor} text-center rounded-pill`}>
					<h5 className="mb-0">{title}</h5>
				</div>
				<div className="applicants-list-count d-flex align-items-center gap-1">
					<i className="ri-group-line"></i>
					<p className="mb-0 fw-semibold">{meta?.totalDocs}</p>
				</div>
			</div>
			<Stack id={`infiniteDiv-${jobStatus}`} className="applicants-list-body gap-4 pt-0">
				<InfiniteScroll
					dataLength={meta?.totalDocs || 0}
					next={loadMoreFunc}
					hasMore={meta?.hasNextPage}
					loader={docs.length >= 1 && <h4>{translate('Common.loading')}...</h4>}
					scrollableTarget={`infiniteDiv-${jobStatus}`}
					scrollThreshold="300px"
					refreshFunction={loadMoreFunc}
					pullDownToRefreshThreshold={50}
					pullDownToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>&#8595; {translate('Common.pullDownToRefresh')}</h3>
					}
					releaseToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>&#8593; {translate('Common.releaseToRefresh')}</h3>
					}
				>
					{docs.length > 0 ? (
						docs?.map((res: any) => (
							<UserCard
								key={`${jobStatus}-${res?.id}`}
								data={res}
								changeStatus={changeStatus}
								currentJobStatus={jobStatus}
								rejectCandidate={rejectCandidateFunction}
							/>
						))
					) : (
						<Stack>{translate('Common.noDataFound')}</Stack>
					)}
				</InfiniteScroll>
			</Stack>
		</div>
	);
};

export default HiringBoardCard;
